import React from 'react';

import { Navigate, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { CircularProgress, Box } from '@mui/material';
import { clearSessionPreservingTab } from '../utils/tabSession';

export default function Protected({ children, role }) {
  const authUser = sessionStorage.getItem('token');
  const location = useLocation();
  const currentPath = location.pathname;
  const userDetails = useSelector((state) => state?.userInfo?.userInfo);
  const isRehydrated = useSelector((state) => state?._persist?.rehydrated);
  const reduxCognitoUser = useSelector(
    (state) => state?.signIn?.signIn?.cognitoRes?.accessToken?.payload?.username
  );
  const sessionCognitoUser = sessionStorage.getItem('cognitoUsername');

  const rolesConfig = userDetails?.roleConfigurations?.authConfiguration;
  if (!authUser) {
    return <Navigate to="/" state={{ from: history.location }} />;
  }

  // Last line of defence: the rehydrated store must belong to the token in this
  // tab. If they disagree the state came from a different account — throw it
  // away and re-authenticate rather than render one company's data under
  // another company's token.
  if (reduxCognitoUser && sessionCognitoUser && reduxCognitoUser !== sessionCognitoUser) {
    clearSessionPreservingTab();
    return <Navigate to="/" state={{ from: location }} />;
  }

  // Still rehydrating redux-persist AND no user details yet — show a loader
  if (!isRehydrated && !userDetails) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh'
        }}>
        <CircularProgress />
      </Box>
    );
  }

  // If rolesConfig is not configured (e.g. Admin role), allow access
  if (!rolesConfig) {
    return children;
  }

  const allowedRoutes = rolesConfig[role] || rolesConfig['defaultRole'];
  const pathMatches = allowedRoutes?.some((route) => {
    const regexPattern = route?.replace(/\/:id$/, '(?:/[a-zA-Z0-9_-]+)?')?.replace(/\//g, '\\/');

    const regex = new RegExp(`^${regexPattern}$`);
    return regex.test(currentPath);
  });

  if (!pathMatches) {
    return <Navigate to="/unauthorized" state={{ from: location }} />;
  }

  return children;
}
